import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatBytes, formatDuration, type PreparedAudio } from '../lib/audio/prepare.js';
import { Spectrogram } from './Spectrogram.js';

interface Props {
  audio: PreparedAudio;
  /** Full card with the spectrogram, or the compact strip that rides along with the questions. */
  expanded: boolean;
  onToggleExpanded: () => void;
  recordAgainLabel: string;
  onRecordAgain: () => void;
  /** A poor-but-usable take, flagged and never refused. */
  warning?: string | null;
  /** Upload status, shown under whichever layout is up. */
  children?: ReactNode;
}

/**
 * The take, once it exists: hear it, see it, or redo it.
 *
 * Two layouts over ONE audio element. Expanded is the "here's what we heard"
 * moment — the spectrogram with a playhead and a scrubber. Compact is the
 * strip from the design: play, a clip line, a thin progress bar and "Record
 * again", small enough to sit above the questions so the take can be checked
 * again while deciding what happened before and after it. Because they share
 * the element, switching between them mid-playback keeps playing, and the two
 * can never overlap.
 *
 * Plays the local `File` through an object URL — no network, and nothing has
 * to be uploaded first. A contributor should be able to confirm the take is
 * the one they meant (the right animal, not just wind) before it goes into a
 * permanent corpus with no edit or delete afterwards.
 */
export function TakeCard({
  audio,
  expanded,
  onToggleExpanded,
  recordAgainLabel,
  onRecordAgain,
  warning,
  children,
}: Props) {
  const { file, durationMs } = audio;
  const audioRef = useRef<HTMLAudioElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    setPlaying(false);
    setPositionMs(0);
    setFailed(false);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  // `timeupdate` only fires a few times a second, which makes a playhead
  // stutter across a spectrogram; follow the clock every frame while playing.
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => {
      const el = audioRef.current;
      if (el) setPositionMs(el.currentTime * 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const toggle = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      return;
    }
    try {
      await el.play();
    } catch {
      setFailed(true);
    }
  };

  const seek = (ms: number) => {
    const el = audioRef.current;
    if (el) el.currentTime = ms / 1000;
    setPositionMs(ms);
  };

  const clamped = Math.min(positionMs, durationMs);
  const fraction = durationMs > 0 ? clamped / durationMs : 0;
  const cantPlay = !url || failed;

  const playButton = (className: string) => (
    <button
      type="button"
      className={className}
      aria-label={playing ? 'Pause the recording' : 'Play the recording'}
      disabled={cantPlay}
      onClick={() => void toggle()}
    >
      {playing ? (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
          <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path
            d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z"
            fill="currentColor"
          />
        </svg>
      )}
    </button>
  );

  const clipLine = `${formatDuration(durationMs)} · ${audio.sampleRateHz / 1000} kHz · ${
    audio.lossless ? 'lossless' : 'lossy'
  }`;

  return (
    <div className={`take-done${expanded ? '' : ' take-done--compact'}`}>
      {/* One element for both layouts, so a switch mid-playback keeps playing. */}
      {url && (
        <audio
          ref={audioRef}
          src={url}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setPositionMs(0);
          }}
          onError={() => setFailed(true)}
        />
      )}

      {expanded ? (
        <>
          <h2 className="take-done-title">Got it — here's what we heard</h2>
          <div className="take-done-frame">
            <div className="take-player-stage">
              <Spectrogram
                samples={audio.peaks}
                sampleRateHz={audio.sampleRateHz}
                durationMs={durationMs}
              />
              <span
                className="take-playhead"
                aria-hidden="true"
                data-visible={playing || clamped > 0}
                style={{ left: `${fraction * 100}%` }}
              />
            </div>

            <div className="take-player-bar">
              {playButton('take-player-btn')}
              <input
                type="range"
                className="take-player-scrub"
                aria-label="Playback position"
                aria-valuetext={`${formatDuration(clamped)} of ${formatDuration(durationMs)}`}
                min={0}
                max={Math.max(1, Math.round(durationMs))}
                step={10}
                value={Math.round(clamped)}
                disabled={cantPlay}
                onChange={(e) => seek(Number(e.target.value))}
              />
              <span className="take-player-time">
                {formatDuration(clamped)} / {formatDuration(durationMs)}
              </span>
            </div>
          </div>

          <div className="take-done-foot">
            <span className="meta">
              {formatDuration(durationMs)} · {formatBytes(file.size)} · {audio.sampleRateHz / 1000}{' '}
              kHz
              {audio.lossless ? ' · lossless' : ' · lossy'}
            </span>
            <span className="take-done-actions">
              <button type="button" className="take-done-again" onClick={onToggleExpanded}>
                Hide spectrogram
              </button>
              <button type="button" className="take-done-again" onClick={onRecordAgain}>
                {recordAgainLabel}
              </button>
            </span>
          </div>
        </>
      ) : (
        <div className="take-strip">
          {playButton('take-strip-btn')}
          <div className="take-strip-body">
            <span className="take-strip-meta">
              {playing
                ? `Playing — ${formatDuration(clamped)} / ${formatDuration(durationMs)}`
                : clipLine}
            </span>
            <div
              className="take-strip-track"
              role="progressbar"
              aria-label="Playback progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(fraction * 100)}
              aria-valuetext={`${formatDuration(clamped)} of ${formatDuration(durationMs)}`}
            >
              <div className="take-strip-fill" style={{ width: `${fraction * 100}%` }} />
            </div>
          </div>
          <div className="take-strip-links">
            <button type="button" className="take-strip-link" onClick={onRecordAgain}>
              {recordAgainLabel}
            </button>
            <button
              type="button"
              className="take-strip-link take-strip-link--quiet"
              aria-expanded={false}
              onClick={onToggleExpanded}
            >
              Spectrogram ▾
            </button>
          </div>
        </div>
      )}

      {failed && (
        <p className="take-player-note" role="status">
          This browser can't play that format back here. The recording itself is fine and will
          upload as normal.
        </p>
      )}

      {warning && (
        <p className="notice notice--info" style={{ margin: '0.9rem 0 0' }}>
          {warning}
        </p>
      )}

      {children && <div className="take-done-status">{children}</div>}
    </div>
  );
}
